import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { sgrColors } from '../../../ui/sgr/SgrColors';

export interface RankedEntry {
  id: string;
  label: string;
  value: number;
  /** Short labels, each shown as a chip to the left of the value */
  tags?: string[];
  href?: string;
}

interface StatisticsRankedListProps {
  entries: RankedEntry[];
  /** Caption shown next to each value, e.g. "Searches" */
  valueLabel: string;
  /** Tooltip for the trailing link button, only used when an entry has an href */
  actionTooltip?: string;
  /** Show each tag's full text on hover, for tags truncated by the chip width */
  showTagTooltips?: boolean;
}

const TOP_RANKS = 3;

const RankBadge = ({ rank }: { rank: number }) => {
  const isTop = rank <= TOP_RANKS;
  return (
    <Box
      sx={{
        width: '1.75rem',
        height: '1.75rem',
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'sgr-albert-sans, sans-serif',
        fontSize: '0.8125rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: isTop ? '#ffffff' : sgrColors.blue,
        backgroundColor: isTop ? sgrColors.navy : sgrColors.grey_cloud,
      }}
    >
      {rank}
    </Box>
  );
};

const StatisticsRankedList = ({
  entries,
  valueLabel,
  actionTooltip,
  showTagTooltips = true,
}: StatisticsRankedListProps) => {
  // The bars encode magnitude relative to the leader, so every row is
  // comparable at a glance without reading the numbers
  const maxValue = entries.reduce((max, entry) => Math.max(max, entry.value), 0);

  return (
    <Box component="ul" sx={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {entries.map((entry, index) => (
        <Box
          component="li"
          key={entry.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.875rem',
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${sgrColors.grey_cloud}`,
            transition: 'background-color 120ms ease-in-out',
            '&:last-of-type': { borderBottom: 'none' },
            '&:hover': { backgroundColor: sgrColors.hover_sky_blue },
          }}
        >
          <RankBadge rank={index + 1} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              <Tooltip title={entry.label} enterDelay={600}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: sgrColors.mine_shaft,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.label}
                </Typography>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.375rem',
                  flexShrink: 0,
                }}
              >
                {entry.tags?.map((tag) => (
                  // The chip has a fixed width, so long tags are truncated and shown in full on hover
                  <Tooltip
                    key={tag}
                    title={showTagTooltips ? tag : ''}
                    enterDelay={300}
                  >
                    <Chip
                      size="small"
                      label={tag}
                      sx={{
                        alignSelf: 'center',
                        // Fixed width so every tag lines up regardless of its length
                        width: '5rem',
                        height: '1.25rem',
                        marginRight: '0.25rem',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        color: sgrColors.navy,
                        backgroundColor: sgrColors.chips_background,
                        border: `1px solid ${sgrColors.grey_cloud}`,
                      }}
                    />
                  </Tooltip>
                ))}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: sgrColors.mine_shaft,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {entry.value.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: sgrColors.backdrop }}>
                  {valueLabel}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                marginTop: '0.375rem',
                height: '0.375rem',
                borderRadius: '0.25rem',
                backgroundColor: sgrColors.grey_cloud,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${maxValue > 0 ? (entry.value / maxValue) * 100 : 0}%`,
                  minWidth: entry.value > 0 ? '0.25rem' : 0,
                  borderRadius: '0.25rem',
                  backgroundColor: sgrColors.navy,
                }}
              />
            </Box>
          </Box>
          {entry.href && (
            <Tooltip title={actionTooltip ?? ''}>
              <IconButton
                component="a"
                href={entry.href}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  flexShrink: 0,
                  color: sgrColors.navy,
                  '&:hover': { color: sgrColors.azure },
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default StatisticsRankedList;
